import React, { useState } from 'react';
import styled from 'styled-components';

const DrillDownSuggestion = ({ suggestion, onSelectTheme }) => {
  const [expanded, setExpanded] = useState(suggestion?.defaultExpanded ?? true);  // Use server config, fallback to true

  if (!suggestion) return null;

  const handleThemeClick = (theme) => {
    // User selected a theme to drill down
    onSelectTheme(theme);
  };

  return (
    <Container>
      <ToggleButton onClick={() => setExpanded(!expanded)}>
        <Icon>{expanded ? '▼' : '▶'}</Icon>
        <Title>💡 Drill Down Available</Title>
        <Badge>{suggestion.themes?.length || 0} options</Badge>
      </ToggleButton>
      
      {expanded && (
        <ContentContainer>
          <Message>{suggestion.message}</Message>
          
          {suggestion.themes && suggestion.themes.length > 0 && (
            <ThemesGrid>
              {suggestion.themes.map((theme, index) => (
                <ThemeCard 
                  key={index}
                  onClick={() => handleThemeClick(theme)}
                >
                  <ThemeLabel>{theme.label}</ThemeLabel>
                  <ThemeConfidence>
                    {(theme.confidence * 100).toFixed(0)}% match
                  </ThemeConfidence>
                </ThemeCard>
              ))}
            </ThemesGrid>
          )}
          
          <HelpText>
            💡 Click any theme above to explore it in detail, or continue viewing the broader {suggestion.category} insights below.
          </HelpText>
        </ContentContainer>
      )}
    </Container>
  );
};

export default DrillDownSuggestion;

const Container = styled.div`
  margin: 1rem 0;
  border: 1px solid #4CAF50;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f8f4;
`;

const ToggleButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #e8f5e9;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #dcedc8;
  }
`;

const Icon = styled.span`
  font-size: 0.8rem;
  color: #4CAF50;
`;

const Title = styled.span`
  font-weight: 600;
  color: #2E7D32;
  flex-grow: 1;
  text-align: left;
`;

const Badge = styled.span`
  background: #4CAF50;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const ContentContainer = styled.div`
  padding: 1.5rem;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Message = styled.div`
  color: #2E7D32;
  font-size: 1rem;
  line-height: 1.6;
  padding: 1rem;
  background: #f1f8f4;
  border-left: 4px solid #4CAF50;
  border-radius: 4px;
  
  strong {
    font-weight: 600;
    color: #1B5E20;
  }
`;

const ThemesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
  margin: 0.5rem 0;
`;

const ThemeCard = styled.button`
  padding: 1rem;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  
  &:hover {
    border-color: #4CAF50;
    background: #f1f8f4;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(76, 175, 80, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ThemeLabel = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
`;

const ThemeConfidence = styled.div`
  font-size: 0.8rem;
  color: #4CAF50;
  font-weight: 500;
`;

const HelpText = styled.div`
  font-size: 0.85rem;
  color: #666;
  font-style: italic;
  text-align: center;
  padding: 0.75rem;
  background: #f9f9f9;
  border-radius: 4px;
`;
